import {
  ChangeEvent,
  MouseEvent,
  ReactElement,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {motion} from 'framer-motion';
import {useDropDown} from '../../../../hooks';
import {Label} from '../../../atoms/label';
import {FieldWrapper} from '../../../atoms/field-wrapper';
import {Helper} from '../../../atoms/helper';
import {Avatar} from '@ui/atoms/avatar';
import {
  Wrapper,
  DropdownWrapper,
  DropWrapper,
  DropdownHandler,
  HandlerWrapper,
  ArrowIconWrapper,
  OptionsWrapper,
} from './styled';
import {MagnifyingGlassIcon} from '../../../atoms/icons';
import {Tooltip} from '@ui/molecules/tooltip';
import {ArrowHeadDownIcon} from '../../../atoms/icons/arrow-head-down';
import {SearchBox} from '../../search-box';
import {Checkbox} from '../../../atoms/checkbox';
import {useStoreContext} from '@store/store-context';
import {HorizontalSpacer} from '../../../atoms/spacer';
import {FlexRow} from '@ui/style/styles';
import {InputNoBorder} from '@ui/atoms/input/input';
import {Groups} from '../../groups';
import {GreenWhitePlusIcon} from '@ui/atoms/icons/greenWhitePlus';
import {Body2, CTA} from '../../../atoms/typography';
import {
  OptionsMultiSelect,
  OptionsMultiSelectAll,
} from '../options-multi-select/options-multi-select';
import {
  SelectOptionItem,
  GroupTypeOption,
  OptionValue,
  TOptionsSelectFieldProps,
} from './types';

const AvatarWrapper = ({items}: {items: SelectOptionItem[]}) => {
  const {
    usersStore: {users},
  } = useStoreContext();

  return (
    <>
      {users
        .filter((user) => user.id === items[0].value)
        .map((user) => (
          <div key={user.id} style={{display: 'flex', alignItems: 'center'}}>
            <Avatar
              src={user.avatar?.url}
              name={
                user?.firstName
                  ? user?.firstName + ' ' + user?.lastName
                  : user?.email
              }
              size="lg"
              userId={user.id}
              tooltip={true}
            />
            <Tooltip
              text={
                user?.firstName
                  ? user?.firstName + ' ' + user?.lastName
                  : user?.email
              }
              bodyStyle={{marginLeft: '6px', fontSize: '16px'}}
              maxLength={13}
              withEllipsis={true}
              tooltipBody={
                user?.firstName
                  ? user?.firstName + ' ' + user?.lastName
                  : user?.email
              }
            />
          </div>
        ))}
      {items.length > 1 && (
        <Body2 weight="semibold" kind="purple300" style={{marginLeft: '6px'}}>
          + {items.length - 1} more
        </Body2>
      )}
    </>
  );
};

export const MultiOptionsSelectField = forwardRef(
  (
    {
      label,
      state,
      helper,
      excludeCheckBox,
      placeholder,
      options = [],
      groupType,
      selectAllOption,
      showCancelIcon,
      type,
      height,
      maxSelection,
      margin,
      excludeSearchBox,
      useMainSearchAsDropdown,
      showSearchIcon,
      performance,
      groupDisplayLength,
      searchPlaceholder,
      setClearAll,
      cancelSelection,
      handleCheckboxcheck,
      onChange,
      handleGroupSelect,
      clearOptions,
      reportingStyling,
      showSelections = true,
      noOptionMessage,
      customOptionName,
      filterGroupReporting,
      customOption,
      onClickCustomOption,
      defaultValue,
      disabled,
      clearManagers,
      handleGroupTypeName,
      dropdownHandlerStyle,
      removeSelectedFromDropdown,
      ...rest
    }: TOptionsSelectFieldProps,
    ref: Ref<any>,
  ) => {
    const {handleOpen, ref: dropRef, open, handleClose} = useDropDown();

    const defaultSelections = useMemo<SelectOptionItem[]>(() => {
      if (!defaultValue?.length) return [];
      const defaultIds = defaultValue.map((val) => val.value);
      return options.filter((option) => defaultIds.includes(option.value));
    }, [defaultValue, options]);

    const [selectedOptions, setSelectedOptions] = useState<SelectOptionItem[]>(
      defaultSelections,
    );
    const [groupTypeNames, setGroupTypeNames] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [customSearchValue, setCustomSearchValue] = useState('');

    useEffect(() => {
      setSelectedOptions(defaultSelections);
    }, [defaultSelections]);

    const selectedOptionIds = useMemo<OptionValue[]>(
      () => selectedOptions?.map((option) => option.value) ?? [],
      [selectedOptions],
    );

    const handleGroupTypeNameInternal = useCallback(
      (name: string) => {
        setGroupTypeNames((prev) =>
          prev.includes(name) ? prev : [...prev, name],
        );
        handleGroupTypeName?.(name);
      },
      [handleGroupTypeName],
    );

    const applySelectionChange = useCallback(
      (nextOptions: SelectOptionItem[]) => {
        setSelectedOptions(nextOptions);
        onChange?.(nextOptions);
      },
      [onChange],
    );

    const handleCancelSelection = useCallback(
      (value: string, clearAll?: boolean) => {
        if (clearAll) {
          applySelectionChange([]);
          cancelSelection?.([]);
          clearManagers?.('');
          setClearAll?.(false);
          return;
        }

        const remaining = selectedOptions.filter(
          (option) => option.value !== value,
        );
        applySelectionChange(remaining);
        cancelSelection?.(remaining);
        clearManagers?.(
          remaining.length
            ? remaining.map((opt) => String(opt.value)).join(',')
            : '',
        );
      },
      [
        applySelectionChange,
        selectedOptions,
        cancelSelection,
        clearManagers,
        setClearAll,
      ],
    );

    useEffect(() => {
      if (clearOptions) {
        handleCancelSelection('', true);
      }
    }, [clearOptions, handleCancelSelection]);

    const handleDropdownChange = useCallback(
      (opts: SelectOptionItem[] | null) => {
        const safeOptions = Array.isArray(opts) ? opts : [];
        const sanitizedOptions = safeOptions.filter(
          (option) => option.value !== 'custom',
        );
        applySelectionChange(sanitizedOptions);
      },
      [applySelectionChange],
    );

    const handleGroupOptionToggle = useCallback(
      (option: SelectOptionItem) => {
        const alreadySelected = selectedOptionIds.includes(option.value);
        const nextSelections = alreadySelected
          ? selectedOptions.filter((item) => item.value !== option.value)
          : [...selectedOptions, option];

        applySelectionChange(nextSelections);
      },
      [applySelectionChange, selectedOptionIds, selectedOptions],
    );

    const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    }, []);

    const filteredOptions = useMemo(() => {
      const normalizedSearch = customSearchValue.trim().toLowerCase();

      const availableOptions = normalizedSearch
        ? options.filter((option) =>
            option.label.toLowerCase().includes(normalizedSearch),
          )
        : options;

      if (type === 'users' || removeSelectedFromDropdown === false) {
        return availableOptions;
      }

      return availableOptions?.filter(
        (item) => !selectedOptionIds.includes(item.value),
      );
    }, [
      customSearchValue,
      options,
      removeSelectedFromDropdown,
      selectedOptionIds,
      type,
    ]);

    const customOptionNode = useMemo<ReactElement | null>(() => {
      if (!customOption) return null;

      return (
        <FlexRow
          onClick={onClickCustomOption}
          style={{cursor: 'pointer', margin: '6px 0px 6px 10px'}}>
          <GreenWhitePlusIcon /> <HorizontalSpacer size="12px" />{' '}
          <CTA kind="textDark" style={{fontWeight: 600}}>
            {customOptionName || 'Manage groups'}
          </CTA>
        </FlexRow>
      );
    }, [customOption, customOptionName, onClickCustomOption]);

    const showSelectedChips =
      selectedOptions?.length > 0 && showSelections !== false;

    const renderHandlerContent = () => {
      if (showSelectedChips) {
        if (type === 'users') {
          return <AvatarWrapper items={selectedOptions} />;
        }

        return (
          <>
            {filterGroupReporting && groupTypeNames?.length > 0 ? (
              <Body2 style={{display: 'flex', alignItems: 'center'}}>
                {groupTypeNames[0]}{' '}
                {groupTypeNames?.length === 1 ? ':' : null}
              </Body2>
            ) : null}
            <Groups
              total={selectedOptions?.length}
              items={selectedOptions.map((item) => ({
                id: String(item.value),
                name: item.label,
                value: item.value,
                disabled: item.disabled,
              }))}
              showCancelIcon={showCancelIcon ?? true}
              groupDisplayLength={groupDisplayLength}
              disabled={disabled}
              cancelFx={!disabled ? handleCancelSelection : undefined}
            />
          </>
        );
      }

      if (useMainSearchAsDropdown) {
        return (
          <InputNoBorder
            placeholder={searchPlaceholder}
            value={customSearchValue}
            onChange={(event) => setCustomSearchValue(event.target.value)}
            style={{fontSize: '14px', padding: '5px '}}
          />
        );
      }

      return <p className="greyOut">{placeholder}</p>;
    };

    const renderArrowIcon = () => {
      if (showSearchIcon) {
        return <MagnifyingGlassIcon />;
      }

      return (
        <motion.span
          initial={{rotate: 0}}
          animate={{
            rotate: disabled ? 0 : open ? 180 : 0,
            marginTop: disabled ? '0px' : open ? '-6px' : '0px',
          }}
          transition={{duration: 0.3}}
          key="multi-select-arrow">
          <ArrowHeadDownIcon />
        </motion.span>
      );
    };

    const DropdownComponent = selectAllOption
      ? OptionsMultiSelectAll
      : OptionsMultiSelect;

    return (
      <FieldWrapper margin={Boolean(margin)}>
        <Wrapper>
          <Label aria-label="label">{label}</Label>
          <DropWrapper ref={dropRef}>
            <DropdownHandler
              open={open}
              onClick={handleOpen}
              performance={performance}
              state={state}
              height={height}
              reportingStyling={reportingStyling}
              disabled={disabled}
              style={dropdownHandlerStyle}>
              <HandlerWrapper>
                <OptionsWrapper disabled={disabled}>
                  {renderHandlerContent()}
                </OptionsWrapper>
                <ArrowIconWrapper disabled={disabled}>
                  {renderArrowIcon()}
                </ArrowIconWrapper>
              </HandlerWrapper>
            </DropdownHandler>
            {open && !filterGroupReporting && (
              <DropdownWrapper>
                <DropdownComponent
                  {...rest}
                  onChange={handleDropdownChange}
                  value={selectedOptions}
                  defaultValue={defaultValue}
                  noOptionMessage={noOptionMessage}
                  maxSelection={maxSelection}
                  handleClose={handleClose}
                  customOptionName={customOptionName}
                  onClickCustomOption={onClickCustomOption}
                  excludeSearchBox={excludeSearchBox}
                  ref={ref as any}
                  excludeCheckBox={excludeCheckBox}
                  placeholder={placeholder}
                  options={filteredOptions}
                  disabled={disabled}
                />
              </DropdownWrapper>
            )}
            {filterGroupReporting && open && (
              <DropdownWrapper>
                <div style={{padding: '5px 8px'}}>
                  <SearchBox
                    placeholder="Search group"
                    onChange={handleSearch}
                    style={{height: '44px'}}
                  />
                  <div>
                    {groupType?.map((grp) => (
                      <GroupsSelect
                        key={grp.name}
                        groups={grp}
                        disabled={disabled}
                        selectedIds={selectedOptionIds}
                        handleGroupSelect={handleGroupSelect}
                        handleGroupTypeName={handleGroupTypeNameInternal}
                        handleCheckboxcheck={handleCheckboxcheck}
                        onToggleGroupOption={handleGroupOptionToggle}
                        search={search}
                      />
                    ))}
                  </div>
                  {customOptionNode}
                </div>
              </DropdownWrapper>
            )}
          </DropWrapper>
          <Helper aria-label="helper" state={state} children={helper} />
        </Wrapper>
      </FieldWrapper>
    );
  },
);

interface GroupsSelectProps {
  groups: GroupTypeOption;
  disabled?: boolean;
  search: string;
  selectedIds: OptionValue[];
  handleGroupSelect?: (option: SelectOptionItem) => void;
  handleGroupTypeName: (name: string) => void;
  onToggleGroupOption: (option: SelectOptionItem) => void;
  handleCheckboxcheck?: (event: MouseEvent<HTMLDivElement>) => void;
}

const GroupsSelect = ({
  groups,
  disabled,
  search,
  selectedIds,
  handleGroupSelect,
  handleGroupTypeName,
  onToggleGroupOption,
  handleCheckboxcheck,
}: GroupsSelectProps) => {
  const [openGroupType, setOpenGroupType] = useState(false);

  const filteredGroups = useMemo(
    () =>
      groups?.groups?.filter((item) =>
        search.trim()
          ? item.name.toLowerCase().includes(search.toLowerCase())
          : true,
      ),
    [groups?.groups, search],
  );

  return (
    <>
      <div
        onClick={() => setOpenGroupType(!openGroupType)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: '8px 8px',
        }}>
        <Body2
          kind={!openGroupType ? 'textBody' : 'textDark'}
          style={openGroupType ? {fontWeight: 500} : {fontWeight: 400}}>
          {groups?.name}
        </Body2>
        <ArrowIconWrapper disabled={disabled}>
          <motion.span
            initial={{rotate: 0}}
            animate={{
              rotate: !disabled && openGroupType ? 180 : 0,
              marginTop: !disabled && openGroupType ? '-6px' : '0px',
            }}
            transition={{duration: 0.3}}
            key="group-select-toggled">
            <ArrowHeadDownIcon />
          </motion.span>
        </ArrowIconWrapper>
      </div>
      {openGroupType && (
        <div
          style={{
            background: '#FAFAFA',
            borderRadius: '0px 0px 8px 8px',
          }}>
          {filteredGroups?.map((item) => (
            <div
              key={item?.id}
              style={{display: 'flex', padding: '8px', cursor: 'pointer'}}
              onClick={(event) => {
                handleGroupTypeName(groups?.name);
                handleCheckboxcheck?.(event);
                handleGroupSelect?.({
                  label: item?.name,
                  value: item.id,
                });
                onToggleGroupOption({
                  label: item?.name,
                  value: item.id,
                });
              }}>
              <Checkbox
                kind="import"
                id={String(item?.id)}
                checked={selectedIds?.includes(item?.id)}
              />
              <Body2 kind="textBody" style={{marginLeft: '8px'}}>
                {item?.name}
              </Body2>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
